import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Filter, ArrowDown, Lightbulb } from "lucide-react";

interface FunnelData {
  sent: number;
  visited: number;
  reviewed: number;
}

interface FunnelViewProps {
  funnel: FunnelData;
  isLoading?: boolean;
}

function getTip(funnel: FunnelData): { message: string; type: "success" | "warning" | "info" } {
  const visitedRate = funnel.sent > 0 ? (funnel.visited / funnel.sent) * 100 : 0;
  const reviewedRate = funnel.visited > 0 ? (funnel.reviewed / funnel.visited) * 100 : 0;

  if (funnel.sent === 0) {
    return {
      message: "Start sending review requests to see your conversion funnel!",
      type: "info",
    };
  }

  if (visitedRate < 30) {
    return {
      message: "Try improving your email subject lines or call-to-action to increase visits.",
      type: "warning",
    };
  }

  if (reviewedRate < 70 && funnel.visited > 0) {
    return {
      message: "Consider simplifying your review page to increase completions.",
      type: "warning",
    };
  }

  return {
    message: "Great job! Your review funnel is converting well.",
    type: "success",
  };
}

export function FunnelView({ funnel, isLoading }: FunnelViewProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Conversion Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const visitedPercent = funnel.sent > 0 ? Math.round((funnel.visited / funnel.sent) * 100) : 0;
  const reviewedPercent = funnel.sent > 0 ? Math.round((funnel.reviewed / funnel.sent) * 100) : 0;
  
  const tip = getTip(funnel);

  const steps = [
    {
      label: "Initiated",
      description: "Email requests + QR scans",
      count: funnel.sent,
      percent: 100,
    },
    {
      label: "Visited Page",
      description: "Opened the review form",
      count: funnel.visited,
      percent: visitedPercent,
    },
    {
      label: "Left Review",
      description: "Completed a rating",
      count: funnel.reviewed,
      percent: reviewedPercent,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Conversion Funnel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {steps.map((step, index) => (
            <div key={step.label}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-medium">{step.label}</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    {step.description}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-bold">{step.count.toLocaleString()}</span>
                  <span className="text-sm text-muted-foreground ml-1">
                    ({step.percent}%)
                  </span>
                </div>
              </div>
              <Progress value={step.percent} className="h-3" />
              {index < steps.length - 1 && (
                <div className="flex justify-center my-2">
                  <ArrowDown className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Tip Section */}
        <div
          className={`mt-6 p-3 rounded-lg flex items-start gap-3 ${
            tip.type === "success"
              ? "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300"
              : tip.type === "warning"
              ? "bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-300"
              : "bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300"
          }`}
        >
          <Lightbulb className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{tip.message}</p>
        </div>
      </CardContent>
    </Card>
  );
}
